// @ts-check
/**
 * peace-hub-port/ResourceDetail.jsx — presentational primitives for the
 * organization detail surface (Block 6.F.3 → X.6.6B design-refresh reskin).
 *
 * Section card wrapper, Field label/value rows, ContactsList navigator,
 * AttachmentsGallery (UI skeleton), CmNotes badge-list, ActionButtons row.
 * The page (OrganizationDetail.jsx) composes these with the peace-os edit
 * infrastructure (EditableField, SuggestEditButton, Pickers) inside each
 * Field's `value` slot — that composition + every prop/export/test id here is
 * unchanged. X.6.6B migrates the styling to x.6.1 tokens (paper surface,
 * shadow/radius, sage/ink text) + Lucide icons; note-type badge colors stay as
 * semantic identity data.
 */

import { Phone, Globe, Pencil, Trash2, ArrowRight } from 'lucide-react';
import { formatFileSize, formatRelative } from '@/lib/format';
import { SectionBlock } from '@/components/SectionBlock';
import { Field as PrimitiveField } from '@/components/primitives/Field';
import { Button } from '@/components/primitives/Button';

// ============================================================
// SECTION CARD WRAPPER
// ============================================================
/**
 * @param {{ title: string, children: React.ReactNode }} props
 */
export function Section({ title, children }) {
  // UX-13 S3 — delegates to SectionBlock's card variant (§6 DataSection
  // ruling): serif text-lg title, sh1 (on detail pages the entity is the
  // page, so sections sit flatter than the directory's sh2 cards).
  return (
    <SectionBlock title={title} variant="card">
      {children}
    </SectionBlock>
  );
}

// ============================================================
// FIELD LABEL/VALUE ROW
// ============================================================
/**
 * @param {{ label: string, value: React.ReactNode }} props
 */
export function Field({ label, value }) {
  // UX-13 S3 — delegates to the Field primitive's display mode (§6 FieldRow
  // ruling: labels are metadata, metadata is ink-soft — recolored from
  // sage-400). The mb-3 rhythm between rows stays the composer's.
  return (
    <div className="mb-3 last:mb-0 break-words">
      <PrimitiveField label={label} mode="display">
        {value}
      </PrimitiveField>
    </div>
  );
}

// ============================================================
// CM NOTES BADGE STYLES — semantic identity (migration 0055 note_type)
// ============================================================
// UX-13 §4 — note-type badges consolidate onto the clinical trio: Tip wears
// the brand's affirm green, Warning the caution gold, General stays neutral
// sand. The retired Tailwind green-50/amber-50 pairs are gone; these classes
// mirror Chip's affirm/caution/neutral treatments.
export const NOTE_TYPE_STYLES = {
  tip: { label: 'Tip', classes: 'bg-tick-affirm text-affirm-deep' },
  warning: { label: 'Warning', classes: 'bg-gold-soft text-caution-deep' },
  general: { label: 'General', classes: 'bg-sand text-ink-soft' },
};

// ============================================================
// CONTACTS LIST — people at this organization
// ============================================================
/**
 * @param {{
 *   contacts: Array<{ id: string, full_name: string, title: string | null,
 *     address?: string | null }>,
 *   onGoToContact: (id: string) => void,
 *   onAddPerson?: () => void,
 * }} props
 *
 * Item 3 (hotfix 2026-06-22): when `onAddPerson` is provided, the card always
 * renders (even with zero contacts) and shows a "+ Add a person" affordance
 * plus an empty-state line. Callers that omit `onAddPerson` keep the prior
 * behavior — null when there are no contacts.
 */
export function ContactsList({ contacts, onGoToContact, onAddPerson }) {
  const hasContacts = Boolean(contacts && contacts.length > 0);
  if (!hasContacts && !onAddPerson) return null;

  // Emptiness is quiet (design-language §4): a zero-item section does not
  // earn a full card. When there are no people yet, collapse to one line
  // (title + inviting copy + the Add door) instead of a full Section card.
  if (!hasContacts) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-1">
        <span className="font-display text-lg text-sage-800">Contacts</span>
        <span className="text-sm text-ink-soft">
          No people linked yet. Add the first to connect a face to this listing.
        </span>
        {onAddPerson && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddPerson}
            data-testid="org-add-person"
            icon={<span aria-hidden="true">+</span>}
          >
            Add a person
          </Button>
        )}
      </div>
    );
  }

  return (
    <Section title={`Contacts (${contacts.length})`}>
      <div className="space-y-1">
        {contacts.map((contact) => (
            <button
              key={contact.id}
              type="button"
              onClick={() => onGoToContact(contact.id)}
              className="w-full text-left px-3 py-2.5 rounded-base hover:bg-sand transition-colors cursor-pointer flex items-center gap-3 group"
            >
              <div className="w-8 h-8 rounded-pill bg-sage-800 text-cream flex items-center justify-center text-sm font-semibold font-display shrink-0">
                {contact.full_name.charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-sage-800 truncate">
                  {contact.full_name}
                </p>
                {contact.title && (
                  <p className="text-xs text-ink-soft truncate">{contact.title}</p>
                )}
                {contact.address && (
                  <p className="text-xs text-sage-300 truncate">{contact.address}</p>
                )}
              </div>
              <ArrowRight size={14} aria-hidden="true" className="text-sage-300 group-hover:text-sage-400 shrink-0 transition-colors" />
            </button>
          ))}
      </div>
      {onAddPerson && (
        <Button
          variant="secondary"
          className="mt-3"
          onClick={onAddPerson}
          data-testid="org-add-person"
          icon={<span aria-hidden="true">+</span>}
        >
          Add a person
        </Button>
      )}
    </Section>
  );
}

// ============================================================
// ATTACHMENTS GALLERY — UI skeleton (every consumer passes [])
// ============================================================
/**
 * @param {{ attachments?: Array<{
 *   url: string, filename: string, type?: string,
 *   thumbnailUrl?: string, size?: number,
 * }> }} props
 */
export function AttachmentsGallery({ attachments }) {
  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => a.type?.startsWith('image/'));
  const files = attachments.filter((a) => !a.type?.startsWith('image/'));

  return (
    <Section title={`Attachments (${attachments.length})`}>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {images.map((att, i) => (
            <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="block">
              <img
                src={att.thumbnailUrl || att.url}
                alt={att.filename}
                className="w-full aspect-square object-cover rounded-card border border-sage-800/[8%] hover:border-sage-400/30 transition-colors"
              />
              <p className="text-[10px] text-ink-soft mt-1 truncate">{att.filename}</p>
            </a>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((att, i) => (
            <a key={i} href={att.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-3 py-2.5 bg-sand rounded-card border border-sage-800/[8%] hover:border-sage-400/30 transition-colors no-underline">
              <Globe size={16} aria-hidden="true" className="text-sage-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-sage-600 font-medium truncate">{att.filename}</p>
                {att.size !== undefined && <p className="text-[11px] text-ink-soft">{formatFileSize(att.size)}</p>}
              </div>
              <ArrowRight size={14} aria-hidden="true" className="text-sage-300 shrink-0" />
            </a>
          ))}
        </div>
      )}
    </Section>
  );
}

// ============================================================
// CM NOTES — versioned annotations with type badges
// ============================================================
/**
 * @param {{
 *   notes?: ReadonlyArray<import('@/lib/types').ProviderNote>,
 *   onEditNote?: ((note: import('@/lib/types').ProviderNote) => void) | null,
 *   onDeleteNote?: ((note: import('@/lib/types').ProviderNote) => void) | null,
 * }} props
 */
export function CmNotes({ notes, onEditNote, onDeleteNote }) {
  if (!notes || notes.length === 0) return null;

  return (
    <Section title={`Notes (${notes.length})`}>
      <div className="space-y-3">
        {notes.map((note) => {
          const typeMeta = NOTE_TYPE_STYLES[note.note_type] ?? NOTE_TYPE_STYLES.general;
          const author = note.is_anonymous ? 'Anonymous' : note.author_display_name;
          return (
            <div key={note.id} className="border border-sage-800/[8%] rounded-card p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-tight ${typeMeta.classes}`}>
                  {typeMeta.label}
                </span>
                <span className="text-xs text-ink-soft">{author}</span>
                <span className="text-xs text-sage-300 ml-auto">{formatRelative(note.created_at)}</span>
                {onEditNote && (
                  <button
                    type="button"
                    onClick={() => onEditNote(note)}
                    aria-label="Edit note"
                    title="Edit note"
                    data-testid={`edit-note-${note.id}`}
                    className="text-sage-400 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded px-1 leading-none"
                  >
                    <Pencil size={13} aria-hidden="true" />
                  </button>
                )}
                {onDeleteNote && (
                  <button
                    type="button"
                    onClick={() => onDeleteNote(note)}
                    aria-label="Delete note"
                    title="Delete note"
                    data-testid={`delete-note-${note.id}`}
                    className="text-sage-400 hover:text-warm-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-500 rounded px-1 leading-none"
                  >
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                )}
              </div>
              <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{note.body}</p>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ============================================================
// ACTION BUTTONS — Call + Website
// ============================================================
/**
 * @param {{ phone?: string | null, website?: string | null }} props
 */
export function ActionButtons({ phone, website }) {
  if (!phone && !website) return null;

  return (
    <div className="flex gap-2.5 mb-6">
      {phone && (
        <a
          href={`tel:${phone.replace(/[^\d+]/g, '')}`}
          className="flex-1 py-3 bg-sage-800 text-cream rounded-pill font-semibold text-sm flex items-center justify-center gap-1.5 shadow-sh1 hover:bg-forest-700 transition-colors duration-[var(--dur-base)] ease-quiet no-underline"
        >
          <Phone size={16} aria-hidden="true" /> Call
        </a>
      )}
      {website && (
        <a
          href={website.startsWith('http') ? website : `https://${website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-3 bg-paper text-sage-800 border border-sage-800/[8%] rounded-pill font-semibold text-sm flex items-center justify-center gap-1.5 shadow-sh1 hover:border-sage-400/30 hover:shadow-sh2 transition-all duration-[var(--dur-base)] ease-quiet no-underline"
        >
          <Globe size={16} aria-hidden="true" /> Website
        </a>
      )}
    </div>
  );
}
